import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { LenderRetailActivityCountryRowViewModel } from '../../../api/types';
import { formatCount, getCountryLabel, mapCreditDecisions } from '../../../lib';
import { CreditDecisionDatum } from '../../../types';
import { getCountryFlag } from './country';

interface Props {
  rows: LenderRetailActivityCountryRowViewModel[];
  loading?: boolean;
}

const CHART_HEIGHT = 280;

export const CreditDecisions = ({ rows, loading }: Props) => {
  const decisions = useMemo(() => mapCreditDecisions(rows), [rows]);

  const legendGroups = useMemo(() => {
    const groups = new Map<string, CreditDecisionDatum[]>();

    decisions.forEach(datum => {
      const list = groups.get(datum.country) ?? [];
      list.push(datum);
      groups.set(datum.country, list);
    });

    return Array.from(groups.entries());
  }, [decisions]);

  const pieData = decisions.map(({ id, label, value, color }) => ({ id, label, value, color }));
  const total = decisions.reduce((sum, datum) => sum + datum.value, 0);

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="span">
        Credit Decisions
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        {loading ? (
          <Skeleton variant="rounded" height={CHART_HEIGHT} />
        ) : !decisions.length ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No data
          </Typography>
        ) : (
          <Box
            sx={{
              mt: 1,
              gap: 3,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
            }}
          >
            <Stack gap={1.5} sx={{ minWidth: 200 }}>
              {legendGroups.map(([country, items]) => {
                const flag = getCountryFlag(country);

                return (
                  <Stack key={country} gap={0.75}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      {flag && <Box component={flag} sx={{ width: 16, height: 16 }} />}
                      <Typography variant="subtitle2" component="span">
                        {getCountryLabel(country)}
                      </Typography>
                    </Stack>

                    <Stack gap={0.5}>
                      {items.map(item => (
                        <Tooltip key={item.id} title={formatCount(item.value)}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Box
                              sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: item.color, flexShrink: 0 }}
                            />
                            <Typography variant="body2">
                              {item.label} — {((item.value / total) * 100).toFixed(1)}%
                            </Typography>
                          </Stack>
                        </Tooltip>
                      ))}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: CHART_HEIGHT, height: CHART_HEIGHT }}>
                <PieChart
                  width={CHART_HEIGHT}
                  height={CHART_HEIGHT}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  series={[
                    {
                      data: pieData,
                      innerRadius: 70,
                      outerRadius: 120,
                      paddingAngle: 1,
                      cornerRadius: 2,
                      valueFormatter: item => formatCount(item.value),
                    },
                  ]}
                  slotProps={{ legend: { hidden: true } }}
                />

                <Box
                  sx={{
                    inset: 0,
                    display: 'flex',
                    position: 'absolute',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography variant="subtitle2" component="span">
                    Credit Apps:
                  </Typography>
                  <Typography variant="subtitle1">{formatCount(total)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Stack>
  );
};
